import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, Star, ImageOff } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatRp, imageUrl } from "@/lib/api";
import { useStorefrontConfig } from "@/context/StorefrontConfigContext";
import { cardRadiusFor } from "@/lib/designTokens";

export default function ProductCard({ product, compact = false }) {
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const { config } = useStorefrontConfig();

  const cardStyle = config.design.product_card_style;
  const isCompactStyle =
    compact ||
    cardStyle === "compact-image-top" ||
    config.dna.product_card === "minimal";

  // Sprint 3: variant awareness on card (read-only).
  // A variant product ALWAYS renders the "Pilih Varian" CTA (fail-closed:
  // it must never enter the simple-product cart path). Price range is
  // optional metadata attached by the list endpoint when feature-enabled.
  const isVariantProduct = !!product.has_variants;
  const variantPriceMin = product.variant_price_min;
  const variantPriceMax = product.variant_price_max;
  const hasVariantRange = isVariantProduct && typeof variantPriceMin === "number";
  const variantPriceIsRange =
    hasVariantRange && typeof variantPriceMax === "number" && variantPriceMax > variantPriceMin;

  const price = hasVariantRange
    ? variantPriceMin
    : (product.sale_price || product.price);
  const hasDiscount = !isVariantProduct &&
    !!product.sale_price && product.sale_price < product.price;

  const discountPct = hasDiscount
    ? Math.round(
        ((product.price - product.sale_price) / product.price) * 100
      )
    : 0;

  const wished = isWishlisted(product.id);
  const [imageFailed, setImageFailed] = useState(false);
  const image = product.images?.[0];
  const outOfStock = !isVariantProduct && product.stock <= 0;

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      data-card-style={cardStyle}
      style={{ borderRadius: cardRadiusFor(cardStyle) }}
      className="storefront-product-card group relative flex flex-col overflow-hidden color-card"
    >
      <Link
        to={`/produk/${product.slug}`}
        className="storefront-product-media block relative aspect-square overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--brand-primary)]"
      >
        {hasDiscount && (
          <span className="storefront-product-badge left-2.5 color-sale-badge">
            -{discountPct}%
          </span>
        )}

        {product.is_bestseller && (
          <span className="storefront-product-badge right-2.5 color-accent-bg border" style={{ borderColor: "var(--color-accent)" }}>
            Terlaris
          </span>
        )}

        {image && !imageFailed ? (
          <img
            src={imageUrl(image)}
            alt={product.name}
            onError={() => setImageFailed(true)}
            className="w-full h-full object-contain p-2 sm:p-3 group-hover:scale-[1.035] transition-transform duration-500 motion-reduce:transition-none"
            loading="lazy"
          />
        ) : (
          <div className="storefront-image-fallback w-full h-full grid place-items-center text-center text-[color:var(--brand-muted)]">
            <span className="flex flex-col items-center gap-2 px-3">
              <ImageOff size={24} strokeWidth={1.6} aria-hidden="true" />
              <span className="text-[11px] font-semibold">
                Gambar belum tersedia
              </span>
            </span>
          </div>
        )}

        {outOfStock ? (
          <span className="storefront-stock-overlay">Stok Habis</span>
        ) : null}

        <button
          onClick={(e) => {
            e.preventDefault();
            toggleWishlist(product.id);
          }}
          data-testid={`wishlist-btn-${product.slug}`}
          aria-label={
            wished
              ? `Hapus ${product.name} dari wishlist`
              : `Tambahkan ${product.name} ke wishlist`
          }
          className={`storefront-wishlist-button absolute bottom-2.5 right-2.5 grid place-items-center backdrop-blur-md transition-colors ${
            wished
              ? "brand-bg-primary text-white"
              /* intentional: white/90 + #1A1A1A is a fixed high-contrast neutral
                 pill designed to sit atop any product image regardless of tenant
                 palette — a "chrome" element, not a brand surface. */
              : "bg-white/90 text-[#1A1A1A] hover:bg-white"
          }`}
        >
          <Heart size={16} fill={wished ? "white" : "none"} />
        </button>
      </Link>

      <div
        className={`${
          isCompactStyle ? "p-3 sm:p-3.5" : "p-3.5 sm:p-4"
        } flex-1 flex flex-col`}
      >
        <Link to={`/produk/${product.slug}`} className="block">
          <h3 className="text-[13px] sm:text-sm font-semibold leading-[1.45] line-clamp-2 min-h-[2.9em] text-[color:var(--product-title-text)] hover:brand-text-primary transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="mt-2.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {isVariantProduct && variantPriceIsRange && (
            <span
              className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-[color:var(--color-text-secondary)] w-full"
              data-testid={`product-card-variant-label-${product.slug}`}
            >
              Mulai dari
            </span>
          )}
          {isVariantProduct && !hasVariantRange && (
            <span
              className="text-[9px] sm:text-[10px] font-semibold italic text-[color:var(--color-text-secondary)] w-full"
              data-testid={`product-card-variant-nostock-${product.slug}`}
            >
              Varian belum tersedia
            </span>
          )}
          <div
            className="commerce-price font-extrabold text-[15px] sm:text-[17px] tracking-[-0.025em]"
            data-testid={`product-card-price-${product.slug}`}
          >
            {formatRp(price)}
          </div>

          {hasDiscount && (
            <div className="text-[10px] sm:text-[11px] text-[color:var(--color-text-secondary)] line-through">
              {formatRp(product.price)}
            </div>
          )}
        </div>

        <div className="mt-2 flex min-h-4 items-center gap-1.5 text-[10px] sm:text-[11px] text-[color:var(--color-text-secondary)]">
          {product.rating_avg > 0 && (
            <span className="flex items-center gap-0.5">
              {/* intentional: #FFB703 = rating-star yellow, a semantic warning/
                  featured color independent of tenant palette (matches
                  standard e-commerce UX). Not driven by design tokens. */}
              <Star size={11} fill="#FFB703" stroke="#FFB703" />
              {product.rating_avg.toFixed(1)}
            </span>
          )}

          {product.rating_avg > 0 ? (
            <span aria-hidden="true">•</span>
          ) : null}

          <span>{product.sold_count || 0} terjual</span>

          <span className="ml-auto truncate text-[9px] sm:text-[10px] uppercase font-bold text-[color:var(--color-text-secondary)]">
            /{(product.unit || "pcs").toUpperCase()}
          </span>
        </div>

        {!compact && (
          isVariantProduct ? (
            <Link
              to={`/produk/${product.slug}`}
              data-testid={`view-variant-btn-${product.slug}`}
              style={{ borderRadius: "var(--brand-radius)" }}
              className="color-btn-primary storefront-add-button mt-3.5 w-full flex items-center justify-center gap-1.5 font-bold text-xs py-2.5"
            >
              Pilih Varian
            </Link>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                addToCart(product.id, 1);
              }}
              data-testid={`add-to-cart-btn-${product.slug}`}
              disabled={outOfStock}
              style={{ borderRadius: "var(--brand-radius)" }}
              className="color-btn-primary storefront-add-button mt-3.5 w-full flex items-center justify-center gap-1.5 font-bold text-xs py-2.5"
            >
              <ShoppingCart size={14} />
              {outOfStock ? "Habis" : "Tambah"}
            </button>
          )
        )}
      </div>
    </div>
  );
}